import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import RegistroTemperatura from "@/pages/RegistroTemperatura";

import AdminLayout from "@/components/layouts/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import AdminControles from "@/pages/admin/Controles";
import Alertas from "@/pages/admin/Alertas";
import Incidencias from "@/pages/admin/Incidencias";
import IncidenciaDetalle from "@/pages/admin/IncidenciaDetalle";
import Reportes from "@/pages/admin/Reportes";
import Locales from "@/pages/admin/Locales";
import Equipos from "@/pages/admin/Equipos";
import Usuarios from "@/pages/admin/Usuarios";
import Configuracion from "@/pages/admin/Configuracion";
import Planillas from "@/pages/Planillas";
import PlanillaFill from "@/components/planillas/PlanillaFill";
import Tareas from "@/pages/Tareas";
import AdminHome from "@/pages/admin/AdminHome";

import UserLayout from "@/components/layouts/UserLayout";
import Inicio from "@/pages/user/Inicio";
import UserControles from "@/pages/user/Controles";
import Escanear from "@/pages/user/Escanear";
import Historial from "@/pages/user/Historial";
import Perfil from "@/pages/user/Perfil";

function FullLoader() {
  return <div className="grid min-h-screen place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
}

function Protected({ children, role }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "ADMIN" ? "/admin" : "/app"} replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "ADMIN") return <Navigate to={user.onboarding_completed ? "/admin" : "/onboarding"} replace />;
  return <Navigate to="/app" replace />;
}

function ScanResolver() {
  const { token } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (user === null) return;
    if (user === false) { nav("/login", { replace: true }); return; }
    api.get(`/scan/${token}`).then(({ data }) => {
      const eq = data.equipment;
      nav("/app/registro", { replace: true, state: { equipment: {
        id: eq.id, name: eq.name, min_temperature: data.min_temperature, max_temperature: data.max_temperature,
        unit: data.unit, location_name: data.location_name, area_name: data.area_name } } });
    }).catch(() => nav("/app/escanear", { replace: true }));
  }, [user, token, nav]);
  return <FullLoader />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/onboarding" element={<Protected role="ADMIN"><Onboarding /></Protected>} />
            <Route path="/scan/:token" element={<ScanResolver />} />

            <Route path="/admin" element={<Protected role="ADMIN"><AdminLayout /></Protected>}>
              <Route index element={<AdminHome />} />
              <Route path="controles" element={<AdminControles />} />
              <Route path="tareas" element={<Tareas isAdmin />} />
              <Route path="planillas" element={<Planillas isAdmin />} />
              <Route path="alertas" element={<Alertas />} />
              <Route path="incidencias" element={<Incidencias />} />
              <Route path="incidencias/:id" element={<IncidenciaDetalle />} />
              <Route path="reportes" element={<Reportes />} />
              <Route path="locales" element={<Locales />} />
              <Route path="equipos" element={<Equipos />} />
              <Route path="usuarios" element={<Usuarios />} />
              <Route path="configuracion" element={<Configuracion />} />
            </Route>

            <Route path="/app/registro" element={<Protected><RegistroTemperatura /></Protected>} />
            <Route path="/planillas/llenar" element={<Protected><PlanillaFill /></Protected>} />
            <Route path="/app" element={<Protected><UserLayout /></Protected>}>
              <Route index element={<Inicio />} />
              <Route path="tareas" element={<Tareas />} />
              <Route path="controles" element={<UserControles />} />
              <Route path="planillas" element={<Planillas />} />
              <Route path="escanear" element={<Escanear />} />
              <Route path="historial" element={<Historial />} />
              <Route path="perfil" element={<Perfil />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
